import { useMemo, useState, type ReactElement } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  RefreshCw,
  FilterX,
  ListFilter,
} from 'lucide-react';
import type { QueryListFilter } from '@kirin/api-client/contracts/extensions';
import { ApiError } from '@kirin/api-client/errors';
import type {
  ProjectCoverageCategory,
  ProjectCoverageDomain,
  ProjectUserBasic,
} from '@kirin/api-client/contracts/projects';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useOrgWriteGate } from '@/hooks/useRoleCheck';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { SearchInput } from '@/components/shared/SearchInput';
import { SeverityBadge } from '@/components/policylist/components/SeverityBadge';
import { ColumnFilter } from '@/components/TableComponents/ColumnFilter';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { queryKeys } from '@/hooks/api/queryKeys';
import {
  fetchProjectBySlug,
  fetchProjectCoverage,
  requestProjectCoverageRescan,
} from './api';
import {
  filterAndSortCategories,
  distinctOptions,
  formatFramework,
  MULTISELECT_FIELDS,
  parseStatusKey,
  STATUS_LABEL,
  type CoverageField,
  type CoverageView,
  type StatusKey,
} from './coverageTableView';
import { CoverageBar, ModeBadge, StatusCell } from './coverageDisplay';

/**
 * Single-project page (KIR-3356 / KIR-3517):
 * `/:orgId/inventory/projects/:projectSlug`. Gated by `projects_inventory`
 * (the route) so the project detail is always reachable. The **security
 * coverage** view (tiles + per-catalog domain cards + category table) is the
 * part that requires the `ai_instruction_catalog` flag; when that flag is off we
 * fall back to the plain KIR-3356 project detail rather than a dead route.
 */
export function SingleProjectPage(): ReactElement {
  const { organizationId, projectSlug } = useParams<{
    organizationId: string;
    projectSlug: string;
  }>();
  const orgId = organizationId ?? '';
  const slug = projectSlug ?? '';

  const { flags, isLoading: isLoadingFlags } = useFeatureFlags([
    flagsmithFeatureFlags.AI_INSTRUCTION_CATALOG,
  ]);
  const coverageEnabled =
    flags[flagsmithFeatureFlags.AI_INSTRUCTION_CATALOG] ?? false;

  if (isLoadingFlags) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <BackToProjects orgId={orgId} />
        <p className="text-muted-foreground">Loading project…</p>
      </div>
    );
  }

  // Keyed by org+slug: the router reuses this component across project
  // routes, and per-project view state (the re-scan watch, the selected
  // domain card) must reset rather than leak onto the next project.
  return coverageEnabled ? (
    <ProjectCoverageView key={`${orgId}/${slug}`} orgId={orgId} slug={slug} />
  ) : (
    <ProjectDetailView key={`${orgId}/${slug}`} orgId={orgId} slug={slug} />
  );
}

function BackToProjects({ orgId }: { orgId: string }): ReactElement {
  return (
    <Link
      to={`/${orgId}/inventory?tab=projects`}
      className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="size-4" />
      Back to Projects
    </Link>
  );
}

/**
 * Fallback shown when `ai_instruction_catalog` is off: the plain KIR-3356
 * project detail (repo, users, file counts) — no coverage tiles/cards/table.
 */
function ProjectDetailView({
  orgId,
  slug,
}: {
  orgId: string;
  slug: string;
}): ReactElement {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: queryKeys.projects.detail(orgId, slug),
    queryFn: ({ signal }) => fetchProjectBySlug(orgId, slug, signal),
    enabled: Boolean(orgId && slug),
    staleTime: 30000,
  });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <BackToProjects orgId={orgId} />
        <p className="text-muted-foreground">Loading project…</p>
      </div>
    );
  }
  if (isError || !data) {
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <div className="flex flex-col gap-4 p-6">
        <BackToProjects orgId={orgId} />
        <p className="text-destructive">
          {notFound
            ? 'Project not found.'
            : "Couldn't load this project. Please try again."}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      <BackToProjects orgId={orgId} />
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">{data.name}</h1>
        <p className="text-sm text-muted-foreground">
          Project details from the inventory
        </p>
      </div>
      <ProjectInfoBar
        orgId={orgId}
        slug={slug}
        repoOriginUrl={data.repoOriginUrl}
        users={data.users}
        aiInstructionsCount={data.aiInstructionsCount}
        hooksCount={data.hooksCount}
        lastScannedAt={null}
        showLastScanned={false}
      />
    </div>
  );
}

/** Coverage poll cadence while a queued re-scan is awaited. */
const RESCAN_POLL_INTERVAL_MS = 3_000;
/** Stop polling for a re-scan result after this long (job lost/queue down). */
const RESCAN_WATCH_MAX_MS = 120_000;

/**
 * Security-coverage dashboard (KIR-3517): project metadata, aggregate coverage
 * tiles, a card per AI-instruction catalog, and the selected catalog's category
 * table. Statuses come from the offline coverage scan job (KIR-3516); a
 * project the job has not visited yet renders every category as "Not scanned"
 * with a banner saying so, and the Re-scan button queues a fresh scan.
 */
function ProjectCoverageView({
  orgId,
  slug,
}: {
  orgId: string;
  slug: string;
}): ReactElement {
  // While a queued re-scan is in flight, poll the coverage query until a scan
  // NEWER than the pre-click baseline lands (the scan is an async pg-boss
  // job, and the app disables refetchOnWindowFocus, so without this the page
  // would show pre-scan data until a manual reload). `baseline` is the
  // lastScannedAt captured when the button was clicked (null = never
  // scanned); the watch self-expires so a lost job can't poll forever.
  const [rescanWatch, setRescanWatch] = useState<{
    baseline: string | null;
    startedAt: number;
  } | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: queryKeys.projects.coverage(orgId, slug),
    queryFn: ({ signal }) => fetchProjectCoverage(orgId, slug, signal),
    enabled: Boolean(orgId && slug),
    staleTime: 30000,
    refetchInterval: query => {
      if (!rescanWatch) return false;
      const last = query.state.data?.lastScannedAt ?? null;
      const landed =
        last !== null &&
        (rescanWatch.baseline === null || last > rescanWatch.baseline);
      if (landed || Date.now() - rescanWatch.startedAt > RESCAN_WATCH_MAX_MS) {
        return false;
      }
      return RESCAN_POLL_INTERVAL_MS;
    },
  });

  // KIR-4811: the inventory drawer's drill-through links here with
  // `?catalog=<id>&status=<key>` so the user lands on the catalog and bucket
  // they clicked. A bogus `catalog` falls through to the default selection
  // below, and a bogus `status` is rejected by parseStatusKey.
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedDomainId, setSelectedDomainId] = useState<string | null>(() =>
    searchParams.get('catalog')
  );
  /**
   * The deep-linked bucket is React state seeded once on mount, NOT a value
   * re-derived from `searchParams` each render. Required: the coverage table is
   * keyed by domain, so switching catalog remounts it and re-runs its filter
   * initialiser. Reading the live URL there would hand the new catalog the old
   * catalog's bucket filter, because `setSearchParams` is a router navigation
   * and need not have landed by the commit that remounts the table. Clearing
   * plain state batches with `setSelectedDomainId` in the same commit, so the
   * remounted table always sees `null`.
   */
  const [deepLinkStatus, setDeepLinkStatus] = useState<StatusKey | null>(() =>
    parseStatusKey(searchParams.get('status'))
  );

  const selectDomain = (id: string): void => {
    setSelectedDomainId(id);
    setDeepLinkStatus(null);
    // Cosmetic only — the filter no longer depends on the URL staying clean,
    // but a stale `?status=` would otherwise survive a manual reload.
    if (searchParams.has('catalog') || searchParams.has('status')) {
      const next = new URLSearchParams(searchParams);
      next.delete('catalog');
      next.delete('status');
      setSearchParams(next, { replace: true });
    }
  };

  const queryClient = useQueryClient();
  const { toast } = useToast();
  // Re-scan is a write (queues a scan that writes coverage rows): enabled for
  // owner/manager, disabled for support, hidden for member.
  const writeGate = useOrgWriteGate(orgId);
  const rescan = useMutation({
    mutationFn: () => requestProjectCoverageRescan(orgId, slug),
    onMutate: () => {
      setRescanWatch({
        baseline: data?.lastScannedAt ?? null,
        startedAt: Date.now(),
      });
    },
    onSuccess: () => {
      toast({
        title: 'Re-scan queued',
        description:
          'Security coverage is being re-analyzed — results appear here shortly.',
      });
      // The scan is async; the rescanWatch refetchInterval above keeps
      // polling until a newer scan lands, this just kicks the first refetch.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.projects.coverage(orgId, slug),
      });
    },
    onError: () => {
      setRescanWatch(null);
      toast({
        title: "Couldn't queue the re-scan",
        description: 'Please try again.',
        variant: 'destructive',
      });
    },
  });

  const backLink = <BackToProjects orgId={orgId} />;

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 p-6">
        {backLink}
        <p className="text-muted-foreground">Loading project…</p>
      </div>
    );
  }

  if (isError || !data) {
    // A 404 means the project genuinely doesn't exist (or is hidden); anything
    // else (500, network) is a real failure and must not masquerade as "not
    // found" — that mislabelling hid a missing-migration 500 during KIR-3517.
    const notFound = error instanceof ApiError && error.status === 404;
    return (
      <div className="flex flex-col gap-4 p-6">
        {backLink}
        <p className="text-destructive">
          {notFound
            ? 'Project not found.'
            : "Couldn't load this project's security coverage. Please try again."}
        </p>
      </div>
    );
  }

  const { project, summary, domains, lastScannedAt } = data;
  const neverScanned = lastScannedAt === null;

  // Default selection = first domain (backend sorts active catalogs first).
  const selectedDomain =
    domains.find(d => d.id === selectedDomainId) ?? domains[0] ?? null;

  return (
    <div className="flex flex-col gap-6 p-6">
      {backLink}

      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold">{project.name}</h1>
          <p className="text-sm text-muted-foreground">
            Security coverage across all Kirin policies for this project
          </p>
        </div>
        {!writeGate.hidden && (
          <Button
            variant="outline"
            size="sm"
            disabled={rescan.isPending || writeGate.disabled}
            onClick={() => rescan.mutate()}
          >
            <RefreshCw
              className={cn('size-4', rescan.isPending && 'animate-spin')}
            />
            Re-scan project
          </Button>
        )}
      </div>

      <ProjectInfoBar
        orgId={orgId}
        slug={slug}
        repoOriginUrl={project.repoOriginUrl}
        users={project.users}
        aiInstructionsCount={project.aiInstructionsCount}
        hooksCount={project.hooksCount}
        lastScannedAt={lastScannedAt}
      />

      <section className="flex flex-col gap-2">
        <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Coverage summary, rule catalogs
        </h2>
        <div className="flex flex-wrap gap-3">
          <SummaryTile label="Missing" value={summary.missing} tone="missing" />
          <SummaryTile label="Partial" value={summary.partial} tone="partial" />
          <SummaryTile label="Covered" value={summary.covered} tone="covered" />
          <SummaryTile
            label="Policy categories"
            value={summary.total}
            tone="neutral"
          />
          <SummaryTile
            label="Critical gaps"
            value={summary.criticalGaps}
            tone="missing"
          />
        </div>
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Coverage by policy domain
        </h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {domains.map(domain => (
            <DomainCard
              key={domain.id}
              domain={domain}
              selected={domain.id === selectedDomain?.id}
              onSelect={() => selectDomain(domain.id)}
            />
          ))}
        </div>
      </section>

      {neverScanned && (
        <div
          role="status"
          className="rounded-lg border border-dashed p-3 text-sm text-muted-foreground"
        >
          Not scanned yet — security coverage analysis has not run for this
          project. Categories below show the catalog baseline; each will get a
          Covered / Partial / Missing status once scanning runs.
        </div>
      )}

      {selectedDomain && (
        <DomainTable domain={selectedDomain} initialStatus={deepLinkStatus} />
      )}
    </div>
  );
}

function ProjectInfoBar({
  orgId,
  slug,
  repoOriginUrl,
  users,
  aiInstructionsCount,
  hooksCount,
  lastScannedAt,
  showLastScanned = true,
}: {
  orgId: string;
  slug: string;
  repoOriginUrl: string | null;
  users: ProjectUserBasic[];
  aiInstructionsCount: number;
  hooksCount: number;
  lastScannedAt: string | null;
  /** Hide the "Last scanned" field in the coverage-less detail fallback. */
  showLastScanned?: boolean;
}): ReactElement {
  return (
    <div className="grid grid-cols-2 gap-4 rounded-lg border p-4 sm:grid-cols-3 lg:grid-cols-5">
      <InfoField label="Repo URL">
        <span className="font-mono text-sm">
          {repoOriginUrl ?? 'No repository (folder-only project)'}
        </span>
      </InfoField>
      <InfoField label="Users">
        <span className="text-sm">{formatUsers(users)}</span>
      </InfoField>
      <InfoField label="AI Instructions">
        {aiInstructionsCount > 0 ? (
          <Link
            to={`/${orgId}/inventory?tab=ai-instructions&org-ai-ins.ip[includes]=${encodeURIComponent(slug)}`}
            className="text-sm text-primary hover:underline"
          >
            {formatCount(aiInstructionsCount, 'instruction file')}
          </Link>
        ) : (
          <span className="text-sm">0 instruction files</span>
        )}
      </InfoField>
      <InfoField label="Hooks">
        {hooksCount > 0 ? (
          <Link
            to={`/${orgId}/inventory?tab=hooks&org-hooks.hpr[includes]=${encodeURIComponent(slug)}`}
            className="text-sm text-primary hover:underline"
          >
            {formatCount(hooksCount, 'hook')}
          </Link>
        ) : (
          <span className="text-sm">0 hooks</span>
        )}
      </InfoField>
      {showLastScanned && (
        <InfoField label="Last scanned">
          {lastScannedAt ? (
            <span className="text-sm">
              <RelativeTimeWithTooltip date={lastScannedAt} />
            </span>
          ) : (
            <span className="text-sm text-muted-foreground">Never</span>
          )}
        </InfoField>
      )}
    </div>
  );
}

function InfoField({
  label,
  children,
}: {
  label: string;
  children: ReactElement | ReactElement[];
}): ReactElement {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <div className="truncate">{children}</div>
    </div>
  );
}

const SUMMARY_TONE_CLASS: Record<
  'missing' | 'partial' | 'covered' | 'neutral',
  string
> = {
  missing: 'text-red-600 dark:text-red-400',
  partial: 'text-amber-600 dark:text-amber-400',
  covered: 'text-green-600 dark:text-green-400',
  neutral: 'text-foreground',
};

function SummaryTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'missing' | 'partial' | 'covered' | 'neutral';
}): ReactElement {
  return (
    <div className="flex min-w-[7rem] items-center gap-3 rounded-lg border p-3">
      <span className={`text-2xl font-semibold ${SUMMARY_TONE_CLASS[tone]}`}>
        {value}
      </span>
      <span className="text-sm text-muted-foreground">{label}</span>
    </div>
  );
}

/** Selectable catalog card: title, mode badge, mini count summary, coverage bar. */
function DomainCard({
  domain,
  selected,
  onSelect,
}: {
  domain: ProjectCoverageDomain;
  selected: boolean;
  onSelect: () => void;
}): ReactElement {
  const { summary } = domain;
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        'flex flex-col gap-2 rounded-lg border p-3 text-left transition-colors hover:bg-muted/50',
        selected && 'border-primary ring-1 ring-primary'
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-medium">{domain.title}</span>
        <ModeBadge mode={domain.mode} />
      </div>
      <div className="text-xs text-muted-foreground">
        <span className="text-red-600 dark:text-red-400">
          {summary.missing} miss
        </span>{' '}
        ·{' '}
        <span className="text-amber-600 dark:text-amber-400">
          {summary.partial} part
        </span>{' '}
        ·{' '}
        <span className="text-green-600 dark:text-green-400">
          {summary.covered} cov
        </span>
      </div>
      <CoverageBar summary={summary} />
    </button>
  );
}

function DomainTable({
  domain,
  initialStatus,
}: {
  domain: ProjectCoverageDomain;
  initialStatus?: StatusKey | null;
}): ReactElement {
  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="text-lg font-semibold">{domain.title}</h2>
        <span className="text-sm text-muted-foreground">
          switch cards above to view another catalog
        </span>
      </div>
      {/* key by domain so filters/sort reset when the selected catalog changes */}
      <CoverageTable
        key={domain.id}
        categories={domain.categories}
        initialStatus={initialStatus}
      />
    </section>
  );
}

function CoverageTable({
  categories,
  initialStatus,
}: {
  categories: ProjectCoverageCategory[];
  initialStatus?: StatusKey | null;
}): ReactElement {
  // Seeded with the display LABEL, not the status key: filterAndSortCategories
  // matches a column's human label, so a key here would silently match nothing.
  const [filters, setFilters] = useState<Record<string, QueryListFilter>>(
    (): Record<string, QueryListFilter> =>
      initialStatus
        ? {
            status: {
              field: 'status',
              condition: 'include',
              values: [STATUS_LABEL[initialStatus]],
            },
          }
        : {}
  );
  const [instructionSearch, setInstructionSearch] = useState('');
  const [globalSearch, setGlobalSearch] = useState('');
  const [sort, setSort] = useState<CoverageView['sort']>(null);

  const rows = useMemo(
    () =>
      filterAndSortCategories(categories, {
        filters,
        instructionSearch,
        globalSearch,
        sort,
      }),
    [categories, filters, instructionSearch, globalSearch, sort]
  );
  const optionsByField = useMemo(
    () =>
      new Map(
        MULTISELECT_FIELDS.map(m => [m.id, distinctOptions(categories, m)])
      ),
    [categories]
  );

  const activeFilters =
    Object.values(filters).filter(f => f.values.length > 0).length +
    (instructionSearch.trim() ? 1 : 0) +
    (globalSearch.trim() ? 1 : 0);
  const hasView = activeFilters > 0 || sort !== null;

  const toggleSort = (field: CoverageField): void =>
    setSort(prev => {
      if (!prev || prev.field !== field) return { field, dir: 'asc' };
      return prev.dir === 'asc' ? { field, dir: 'desc' } : null;
    });
  const clearAll = (): void => {
    setFilters({});
    setInstructionSearch('');
    setGlobalSearch('');
    setSort(null);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div className="w-[260px] shrink-0">
            <SearchInput
              value={globalSearch}
              onChange={setGlobalSearch}
              placeholder="Search all columns"
            />
          </div>
          <span className="whitespace-nowrap text-sm text-muted-foreground">
            {rows.length === categories.length
              ? formatCount(categories.length, 'category', 'categories')
              : `${rows.length} of ${categories.length} categories`}
          </span>
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              onClick={clearAll}
              disabled={!hasView}
              className="group h-8 px-3 shadow-sm transition-all hover:border-destructive/50 hover:bg-destructive/10 hover:text-destructive"
            >
              <FilterX className="mr-2 h-3.5 w-3.5 transition-colors group-hover:text-destructive" />
              <span className="text-xs font-medium transition-colors group-hover:text-destructive">
                Clear filters
              </span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Remove all active filters</p>
          </TooltipContent>
        </Tooltip>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">#</TableHead>
              {MULTISELECT_FIELDS.map(m => (
                <TableHead key={m.id}>
                  <div className="flex items-center gap-0.5">
                    <ColumnFilter
                      field={m.id}
                      fieldName={m.header}
                      options={optionsByField.get(m.id) ?? []}
                      currentFilter={filters[m.id]}
                      onFilterChange={f =>
                        setFilters(prev => ({ ...prev, [m.id]: f }))
                      }
                    />
                    <SortLabel
                      field={m.id}
                      header={m.header}
                      sort={sort}
                      onToggle={toggleSort}
                    />
                  </div>
                </TableHead>
              ))}
              <TableHead>
                <div className="flex items-center gap-0.5">
                  <TextColumnFilter
                    fieldName="Example instruction to add"
                    value={instructionSearch}
                    onChange={setInstructionSearch}
                  />
                  <SortLabel
                    field="instruction"
                    header="Example instruction to add"
                    sort={sort}
                    onToggle={toggleSort}
                  />
                </div>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((category, index) => (
              <TableRow key={category.id}>
                <TableCell className="text-muted-foreground">
                  {index + 1}
                </TableCell>
                <TableCell className="font-medium">{category.name}</TableCell>
                <TableCell>
                  <SeverityBadge riskLevel={category.severity} showIcon />
                </TableCell>
                <TableCell>
                  <Badge variant="outline">
                    {formatFramework(category.framework, category.reference)}
                  </Badge>
                </TableCell>
                <TableCell>
                  <StatusCell status={category.status} />
                </TableCell>
                <TableCell className="font-mono text-sm">
                  {category.coveredFilePath ?? '—'}
                </TableCell>
                <TableCell className="max-w-md text-sm text-muted-foreground">
                  {category.status === 'covered' ? '—' : category.instruction}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="py-6 text-center text-sm text-muted-foreground"
                >
                  No categories match the current filters.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

/** Header label that toggles sort for its column (asc → desc → off). */
function SortLabel({
  field,
  header,
  sort,
  onToggle,
}: {
  field: CoverageField;
  header: string;
  sort: CoverageView['sort'];
  onToggle: (field: CoverageField) => void;
}): ReactElement {
  const active = sort?.field === field;
  const Icon = active
    ? sort?.dir === 'asc'
      ? ArrowUp
      : ArrowDown
    : ArrowUpDown;
  return (
    <button
      type="button"
      onClick={() => onToggle(field)}
      className="inline-flex items-center gap-1 hover:text-foreground"
      aria-label={`Sort by ${header}`}
    >
      {header}
      <Icon
        className={cn(
          'size-3.5',
          active ? 'text-foreground' : 'text-muted-foreground/60'
        )}
      />
    </button>
  );
}

/**
 * A textual "contains" column filter — a list-filter trigger (matching the
 * multi-select {@link ColumnFilter}) opening a single free-text input, for
 * columns where a value list would be pointless (the example instruction).
 */
function TextColumnFilter({
  fieldName,
  value,
  onChange,
}: {
  fieldName: string;
  value: string;
  onChange: (value: string) => void;
}): ReactElement {
  const active = value.trim().length > 0;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          className="relative"
          title={`Filter by ${fieldName}`}
        >
          <ListFilter
            className={cn(
              'size-4',
              active ? 'text-primary' : 'text-muted-foreground'
            )}
            strokeWidth={active ? 2.75 : 2}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start">
        <Input
          autoFocus
          placeholder={`Filter ${fieldName.toLowerCase()}…`}
          value={value}
          onChange={e => onChange(e.target.value)}
          aria-label={`Filter by ${fieldName}`}
        />
      </PopoverContent>
    </Popover>
  );
}

/** e.g. `1 instruction file` / `40 instruction files`. Optional explicit plural. */
function formatCount(count: number, noun: string, plural?: string): string {
  if (count === 1) return `${count} ${noun}`;
  return `${count} ${plural ?? `${noun}s`}`;
}

/** `Ada Lovelace, Alan Turing +2` — first two names, then an overflow count. */
function formatUsers(users: ProjectUserBasic[]): string {
  if (users.length === 0) return 'No users';
  const names = users.map(u => u.name ?? u.email ?? u.userId);
  const shown = names.slice(0, 2).join(', ');
  const extra = names.length - 2;
  return extra > 0 ? `${shown} +${extra}` : shown;
}

export default SingleProjectPage;
